import { Component, Input } from "@angular/core";

interface Values {
  totalSKUCount: number;
  assessStrategicValueCount: number;
  assessStrategicValuePassedCount?: number;
  assessStrategicValueFailedCount?: number;
  prioritizationFilterCount: number;
  prioritizationFilterPassedCount?: number;
  financialRecoveryTestCount?: number;
  maintainHighVolumeCount: number;
  maintainLowVolumeCount: number;
  highVolumeProfitableCount: number;
  highVolumeProfitablePassedCount?: number;
  highVolumeProfitableFailedCount?: number;
  focusBrandCount: number;
  lowVolumeProfitableCount: number;
  lowVolumeProfitablePassedCount?: number;
  lowVolumeProfitableFailedCount?: number;
  highVolumeProfitableKeepCount?: number;
  highVolumeProfitableDtpCount?: number;
  highVolumeProfitableOptimizeCount?: number;
  growCount?: number;
  accelerateCount?: number;
  lowVolumeProfitableKeepCount?: number;
  lowVolumeProfitableDtpCount?: number;
  lowVolumeProfitableOptimizeCount?: number;
  delistCount?: number;
  focusBrandPassedCount?: number;
  focusBrandFailedCount?: number;
  prioritizationFilterFailedCount?: number;
  financialRecoveryTestPassedCount?: number;
  financialRecoveryTestFailedCount?: number;
  vilcRefCount?: number;
  vilcRefPassedCount?: number;
  vilcRefFailedCount?: number;
  isMBCount?: number;
  isMBPassedCount?: number;
  isMBFailedCount?: number;
  volumeRetentionTestCount?: number;
  volumeRetentionTestPassedCount?: number;
  volumeRetentionTestFailedCount?: number;
  overall_decision_tree?: boolean;
}

type ValueKey = keyof Values;

interface PositionConfig {
  key: ValueKey;
  class: string;
  type?: "output";
}

@Component({
  selector: "app-decision-tree",
  templateUrl: "./decision-tree.component.html",
  styleUrls: ["./decision-tree.component.scss"],
})
export class DecisionTreeComponent {
  @Input() values!: Values;
  @Input() hasSellout: boolean = false;
  @Input() hasVilcRef: boolean = false;
  @Input() skuRecommendationVersion: string = "V3";

  private allPositions: PositionConfig[] = [
    { key: "totalSKUCount", class: "n1" },
    { key: "assessStrategicValueCount", class: "n2" },
    { key: "prioritizationFilterCount", class: "n3" },
    { key: "financialRecoveryTestCount", class: "n4" },
    { key: "maintainHighVolumeCount", class: "n5" },
    { key: "maintainLowVolumeCount", class: "n6" },
    { key: "highVolumeProfitableCount", class: "n7" },
    { key: "focusBrandCount", class: "n8" },
    { key: "lowVolumeProfitableCount", class: "n9" },
    { key: "highVolumeProfitableKeepCount", class: "n10", type: "output" },
    { key: "highVolumeProfitableDtpCount", class: "n11", type: "output" },
    { key: "growCount", class: "n12", type: "output" },
    { key: "delistCount", class: "n13", type: "output" },
    { key: "assessStrategicValuePassedCount", class: "n14" },
    { key: "assessStrategicValueFailedCount", class: "n15" },
    { key: "prioritizationFilterPassedCount", class: "n16" },
    { key: "highVolumeProfitablePassedCount", class: "n17" },
    { key: "highVolumeProfitableFailedCount", class: "n18" },
    { key: "lowVolumeProfitablePassedCount", class: "n19" },
    { key: "lowVolumeProfitableFailedCount", class: "n20" },
    { key: "lowVolumeProfitableKeepCount", class: "n21", type: "output" },
    { key: "lowVolumeProfitableDtpCount", class: "n22", type: "output" },
    { key: "focusBrandPassedCount", class: "n23" },
    { key: "focusBrandFailedCount", class: "n24" },
    { key: "prioritizationFilterFailedCount", class: "n25" },
    { key: "financialRecoveryTestFailedCount", class: "n26" },
    { key: "financialRecoveryTestPassedCount", class: "n27" },
    { key: "vilcRefCount", class: "n28" },
    { key: "vilcRefPassedCount", class: "n29" },
    { key: "vilcRefFailedCount", class: "n30" },
    { key: "isMBCount", class: "n31" },
    { key: "isMBPassedCount", class: "n32" },
    { key: "isMBFailedCount", class: "n33" },
    { key: "volumeRetentionTestCount", class: "n34" },
    { key: "volumeRetentionTestPassedCount", class: "n35" },
    { key: "volumeRetentionTestFailedCount", class: "n36" },
  ];

  get positions(): PositionConfig[] {
    return this.allPositions
      .filter(
        (pos) =>
          this.hasVilcRef ||
          ![
            "n28",
            "n29",
            "n30",
            "n31",
            "n32",
            "n33",
            "n34",
            "n35",
            "n36",
          ].includes(pos.class),
      )
      .filter(
        (pos) => this.hasSellout || !["n4", "n26", "n27"].includes(pos.class),
      )
      .map((pos) => {
        if (this.skuRecommendationVersion === "V3") {
          if (pos.key === "highVolumeProfitableDtpCount") {
            return { ...pos, key: "highVolumeProfitableOptimizeCount" };
          }
          if (pos.key === "growCount") {
            return { ...pos, key: "accelerateCount" };
          }
          if (pos.key === "lowVolumeProfitableDtpCount") {
            return { ...pos, key: "lowVolumeProfitableOptimizeCount" };
          }
        }
        return pos;
      });
  }

  get imageKey(): string {
    const sellout = this.hasSellout ? "sellout" : "nosellout";
    const vilc = this.hasVilcRef ? "vilc" : "novilc";
    const version = this.skuRecommendationVersion.toLowerCase();
    return `${sellout}-${vilc}-${version}`;
  }

  get warningIconClass(): string {
    const vilcRef = this.hasVilcRef ? "with-vilc-ref" : "without-vilc-ref";
    return `warning-icon-${vilcRef}-${this.skuRecommendationVersion}`;
  }

  getDecisionTreeClass(): string {
    const sellout = this.hasSellout ? "has-sellout" : "no-sellout";
    const vilcRef = this.hasVilcRef ? "has-vilcRef" : "no-vilcRef";
    const version = this.skuRecommendationVersion;
    return `${sellout}-${vilcRef}-${version}`;
  }
}
